import { Inject, Injectable } from '@angular/core';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import {
  IUser,
  CookieList,
  COOKIE_NAMES,
} from '@beneficity/authentication/types';
import { HttpClient } from '@angular/common/http';
import { switchMap } from 'rxjs/operators';
import { LoginCookiesService } from './api/login-cookie.service';
import { EligibilityBrandService } from './api/brand.service';
import { Brand } from '@beneficity/brand/types';
import { ENV_CONFIG, EnvConfig } from '@beneficity/environment/types';
import { OamTokenAuthService } from './api/oam-token-auth.service';
import { LogoutService } from './api/logout.service';
import {
  decodeToken,
  getDecodedAccessToken,
  parseCookieValue,
} from '@beneficity/shared/util';

@Injectable()
export class AuthenticationService {
  /**
   * Build User from Authorize response
   * @param authResponse
   * @private
   */
  private static authorizeResponseToUser(
    brandResponse: any,
    brand: any
  ): IUser {
    let groupNumber;
    let clientNumber;
    // Have implemented the below logic because the Eligibility brand API can either return an array or just object
    // In order to handle both scenarios, had to break it down using if else statements.
    if (Array.isArray(brandResponse)) {
      groupNumber = brandResponse.find(
        (item) => item.ubk === brand.ubk && item.planYear === 'current'
      )?.groupNumber;
      clientNumber = brandResponse.find(
        (item) => item.ubk === brand.ubk && item.planYear === 'current'
      )?.clientNumber;
    } else {
      groupNumber = brandResponse.groupNumber;
      clientNumber = brandResponse.clientNumber;
    }
    return {
      memberId: '', //brandResponse?.payload?.memberId,
      username: '', //brandResponse?.payload?.loginId,
      role: 'MEMBER',
      ubk: brand.ubk,
      applicableUBKs: [brand.ubk],
      firstName: '', //brandResponse?.payload?.firstName?.toLowerCase(),
      lastName: '', //brandResponse?.payload?.lastName?.toLowerCase(),
      groupNumber: groupNumber,
      mbrClientNumber: clientNumber,
      mbrGroupEffectiveDate: '', //brandResponse?.payload?.coverageEffectiveDate,
      planName: '', //brandResponse?.payload?.planName,
      forcedPassword: false, //brandResponse?.payload?.securityStrengthEnforcement?.toUpperCase() === 'FORCED',
      isTempPassword: false,
      avatarUrl: '',
      brand: '',
      isSalesDemo: '', //brandResponse?.payload?.salesDemo,
    };
  }

  /**
   * Pull UBK from Authorize Response
   * @param brandResponse
   * @param defaultOverrideValues
   * @private
   */
  private static getUserBrandFromAuthResponse(
    brandResponse: any,
    defaultOverrideValues: Record<string, string>
  ): number {
    const partnerUbk = this.getUbkFromPartner(defaultOverrideValues);
    let ubk: any;

    if (Array.isArray(brandResponse)) {
      const foundBrand = brandResponse.find(
        (item: any) => item.ubk.toString() === partnerUbk
      );
      ubk = foundBrand?.ubk;
    } else {
      ubk = brandResponse?.ubk;
    }
    return Number.parseInt(ubk, 10);
  }

  /**
   * Pull the brandubk values passed by the vendor
   * @param defaultOverrideValues
   */
  private static getUbkFromPartner(
    defaultOverrideValues: Record<string, string>
  ): string | undefined {
    if (!defaultOverrideValues) {
      return undefined;
    }

    const brandUbkKey = Object.keys(defaultOverrideValues).find(
      (key) => key.toLowerCase() === 'brandubk' && defaultOverrideValues[key]
    );

    return brandUbkKey ? defaultOverrideValues[brandUbkKey] : undefined;
  }

  /**
   * Pull the required Member cookie values from the authorization response
   * @param brandResponse The authorization response, can be a single object or an array of objects.
   * @param userBrand The user's brand information.
   */
  private static getMemberCookies(
    brandResponse: any,
    userBrand: Brand
  ): CookieList {
    const brandCode =
      (userBrand?.umsBrandCode || userBrand?.sharedBrandCode)?.toUpperCase() ||
      undefined;
    let mbrGrpNum: any, mbrCliNum: any, mbrBpdId: any;

    if (Array.isArray(brandResponse)) {
      const matchingBrand = brandResponse.find(
        (item: any) => item.ubk?.toString() === userBrand?.ubk?.toString()
      );

      if (matchingBrand) {
        mbrGrpNum = matchingBrand.groupNumber;
        mbrCliNum = matchingBrand.clientNumber;
        mbrBpdId = matchingBrand.bpdId;
      }
    } else {
      mbrGrpNum = brandResponse?.groupNumber;
      mbrCliNum = brandResponse?.clientNumber;
      mbrBpdId = brandResponse?.bpdId;
    }

    const memberCookies: CookieList = {
      umsBrand: brandCode,
      mbrGroupNumber: mbrGrpNum,
      mbrClientNumber: mbrCliNum,
      emkt_ubk: userBrand?.ubk?.toString(),
    };

    memberCookies.DDValue = mbrBpdId
      ? encodeURIComponent(btoa(mbrBpdId))
      : undefined;

    return memberCookies;
  }

  /**
   * Maps valid override properties into org defined cookies.
   * @param overrides
   * @returns
   */
  private static mapOverridesToCookieVals(overrides: Record<string, string>) {
    const overrideEntries =
      Object.entries(overrides)?.length > 0 ? Object.entries(overrides) : null;
    if (Boolean(overrideEntries)) {
      const overrideCookies: CookieList = {};
      overrideEntries.map(([k, v]) => {
        if (COOKIE_NAMES[k] && Boolean(v)) {
          overrideCookies[COOKIE_NAMES[k]] = v;
        }
      });
      return overrideCookies;
    } else {
      return null;
    }
  }

  constructor(
    private readonly httpClient: HttpClient,
    private readonly loginCookieService: LoginCookiesService,
    private readonly brandService: EligibilityBrandService,
    private readonly oamTokenAuthService: OamTokenAuthService,
    private readonly logoutService: LogoutService,
    @Inject(ENV_CONFIG) private readonly envConfig: EnvConfig
  ) {}

  /**
   * Entry point when we get an Access Token
   * @param accessToken
   */
  public authenticate(
    accessToken: string,
    defaultOverrideValues?: Record<string, string>
  ): Observable<[IUser, Record<string, unknown>, string, Brand, CookieList]> {
    return this.validateToken(accessToken, defaultOverrideValues);
  }

  /**
   * Public method for overwriting cookies if non-default values are required
   * @param cookies
   * @returns
   */
  public updateCookies(
    accessToken: string,
    cookies: Record<string, string>
  ): Observable<any> {
    const isWyomingClient = this.isWyomingClient(accessToken);

    if (isWyomingClient) {
      return forkJoin([of(cookies), of(null)]);
    }
    return forkJoin([
      of(cookies),
      this.loginCookieService.setCookies(JSON.stringify(cookies)),
    ]);
  }

  /**
   * Validate the Access Token
   * Pull legacy token and call login cookie service to set LtpaToken cookie header
   * Call Authorize Member to get member attributes and cookies.
   * @param accessToken
   * @private
   */
  private validateToken(
    accessToken: string,
    defaultOverrideValues?: Record<string, string>
  ): Observable<[IUser, Record<string, unknown>, string, Brand, CookieList]> {
    const tokenInfo = getDecodedAccessToken(accessToken);
    const tokenDomain = tokenInfo.domain;

    return this.httpClient
      .get(
        `https://${this.envConfig.serviceDomains.oam}/api/oauth/v4/token/info?access_token=${accessToken}`,
        {
          headers: {
            Accept: 'application/json',
            'X-OAUTH-IDENTITY-DOMAIN-NAME': tokenDomain,
          },
        }
      )
      .pipe(
        switchMap((attrs) =>
          // callled oamTokenAuthService to obtain the OAM ID,
          // this is new endpoint provided from OAM team
          combineLatest([
            this.oamTokenAuthService.fetch(accessToken, attrs),
            of(attrs),
          ])
        ),
        switchMap(([oamId, attributes]) =>
          this.processLegacyToken(
            oamId as Record<string, unknown>,
            attributes as Record<string, unknown>,
            accessToken
          )
        ),
        switchMap(([attributes, cookies, _]) =>
          this.authorizeMember(
            accessToken,
            attributes,
            cookies,
            defaultOverrideValues
          )
        ),
        switchMap(([user, attributes, brand, cookies]) =>
          forkJoin([
            of(user),
            of(attributes),
            of(accessToken),
            of(brand),
            of(cookies),
          ])
        )
      );
  }

  /**
   * Pull the LtpaToken2 cookie from the access token attributes.
   * Pass token to the login cookie service to attach token as a Cookie header.
   * @param attributes
   * @param OAM_ID which is passed to setCookie method
   * @private
   */
  private processLegacyToken(
    oamId: Record<string, unknown>,
    attributes: Record<string, unknown>,
    accessToken: string
  ): Observable<[Record<string, unknown>, Record<string, string>, any]> {
    const cookies = {
      LtpaToken2: parseCookieValue(
        decodeToken(attributes?.['LtpaToken2'] as string)
      ),
      OAM_ID: atob(oamId?.['OAM_ID'] as string),
    };
    const isWyomingClient = this.isWyomingClient(accessToken);

    if (isWyomingClient) {
      if (!this.envConfig.production) {
        console.log(
          'Wyoming client detected - skipping setCookie service call'
        );
      }
      return forkJoin([of(attributes), of(cookies), of(null)]) as Observable<
        [Record<string, unknown>, Record<string, string>, any]
      >;
    }

    return forkJoin([
      of(attributes),
      of(cookies),
      this.loginCookieService.setCookies(JSON.stringify(cookies)),
    ]) as Observable<[Record<string, unknown>, Record<string, string>, any]>;
  }

  /**
   * Call authorize member to get member attributes.
   * Pull attributes and call member cookie service to attach as Cookie headers.
   * @param attributes
   * @param authCookies
   * @private
   */
  private authorizeMember(
    accessToken: string,
    attributes: Record<string, unknown>,
    authCookies: Record<string, string>,
    defaultOverrideValues?: Record<string, string>
  ): Observable<[IUser, Record<string, unknown>, Brand, CookieList]> {
    return this.brandService.fetch(accessToken, defaultOverrideValues).pipe(
      switchMap((brandResponse) => {
        return forkJoin([
          of(brandResponse),
          this.getBrand(
            AuthenticationService.getUserBrandFromAuthResponse(
              brandResponse,
              defaultOverrideValues
            )
          ),
        ]);
      }),
      switchMap(([brandResponse, brand]) => {
        let cookies = AuthenticationService.getMemberCookies(
          brandResponse,
          brand
        );
        // Cookie object assembled in priority order auth > default > member

        if (Boolean(defaultOverrideValues)) {
          cookies = {
            ...cookies,
            ...AuthenticationService.mapOverridesToCookieVals(
              defaultOverrideValues
            ),
            ...authCookies,
          };
        } else {
          cookies = {
            ...cookies,
            ...authCookies,
          };
        }

        const isWyomingClient = this.isWyomingClient(accessToken);

        if (isWyomingClient) {
          if (!this.envConfig.production) {
            console.log(
              'Wyoming client detected - skipping setCookie service call'
            );
          }
          return forkJoin([
            of(brandResponse),
            of(brand),
            of(cookies),
            of(null),
          ]);
        }

        // Call backend to validate and set member cookies as cookie headers
        return forkJoin([
          of(brandResponse),
          of(brand),
          of(cookies),
          this.loginCookieService.setCookies(JSON.stringify(cookies)),
        ]);
      }),
      switchMap(([brandResponse, brand, cookies]) => {
        return forkJoin([
          of(
            AuthenticationService.authorizeResponseToUser(brandResponse, brand)
          ),
          of(attributes),
          of(brand),
          of(cookies),
        ]);
      })
    );
  }

  public getBrand(ubk: number): Observable<Brand> {
    const brandContentBasePath = '/assets/content/brand/';
    const brandValuesUrl = `${brandContentBasePath}${ubk}.json`;

    return this.httpClient.get(brandValuesUrl);
  }

  public logout(domain): Observable<any> {
    return combineLatest([
      this.logoutService.logoutByDomain(domain),
      this.logoutService.logout(),
    ]);
  }

  /**
   * Checks if the current user is a Wyoming client based on token scope
   * @param accessToken - JWT access token
   * @returns true if user has Wyoming scope
   */
  private isWyomingClient(accessToken: string): boolean {
    if (!accessToken) {
      return false;
    }

    try {
      const decodedToken = this.decodeToken(accessToken);
      const scope = decodedToken?.scope || '';
      return (
        scope.includes('engen.health-plan.bcbswy') &&
        !scope.includes('customer-service.read-only')
      );
    } catch (error) {
      console.error('Error checking Wyoming client scope:', error);
      return false;
    }
  }

  /**
   * Decodes JWT token to extract payload
   * @param token - JWT access token
   * @returns Decoded token payload
   */
  private decodeToken(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }
}
